import * as React from "react";
import { connect } from "react-redux";
import { QueryTable, QueryTableColumn } from "../../misc/components/queryTable/QueryTable";
import { Query, User, QueryCondition, Permission } from "../../misc/api/apiTypes";
import MultiSelectModal from '../../misc/components/queryTable/MultiSelectModal';
import { HamburgerMenu } from "../../misc/components/HamburgerMenu";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { Authorization, GlobalAuthorization } from "../../misc/components/Authorization";
import Condition from "../../misc/components/Condition";
import { MainState } from "../../Core/reducers";
import BooleanSelectModal from "../../misc/components/queryTable/BooleanSelectModal";
import { QueryTableState, ReceiveQueryAction, PageAction, SortAction, FilterAction } from "../../misc/components/queryTable/Hooks/UseQueryTable";
import { useSecurityContext } from "../../Core/SecurityContext";
import { Link } from "react-router-dom";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { uniqBy } from "../../misc/ArrayFunctions";

interface UserListTableState {
    showEmailFilter: boolean;
    showFirstNameFilter: boolean;
    showLastNameFilter: boolean;
    showIsDisabledFilter: boolean;
    emailFilterQuery: Query<User>;
}

const initialUserTableState: UserListTableState = {
    showEmailFilter: false,
    showFirstNameFilter: false,
    showLastNameFilter: false,
    showIsDisabledFilter: false,
    emailFilterQuery: {
        Filters: [],
        Members: ['Id', 'Email', 'FirstName', 'LastName'],
    },
}

export interface UserListTableProps {
    queryTableReducer: [QueryTableState, React.Dispatch<any>];
    currentStudyId: string;
}

export const UserListTable = ({ queryTableReducer, currentStudyId }: UserListTableProps) => {
    const { currentUserPermissions } = useSecurityContext();
    const [queryTableState, dispatch] = queryTableReducer;
    const legacyClient = useWebPortalLegacyClient();
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        let mounted = true;
        const fetchUsers = async () => {
            if (mounted) {
                setIsLoading(true);
                const userQuery = await legacyClient.getUsers(queryTableState.query);
                dispatch(ReceiveQueryAction.create(userQuery));
                setIsLoading(false);
            }
        }

        fetchUsers();

        return () => { mounted = false };
    }, [queryTableState.query.Page.Current, queryTableState.query.Order.Member, queryTableState.query.Order.Direction, queryTableState.query.Filters]);

    // Non-generic
    const userReducer = (state: UserListTableState, action): UserListTableState => {
        switch (action.type) {
            case 'SetEmailsFilterVisiblity':
                return {
                    ...state,
                    showEmailFilter: action.isVisible,
                };
            case 'SetFirstNameFilterVisiblity':
                return {
                    ...state,
                    showFirstNameFilter: action.isVisible,
                };
            case 'SetLastNameFilterVisiblity':
                return {
                    ...state,
                    showLastNameFilter: action.isVisible,
                };
            case 'SetIsDisabledFilterVisiblity':
                return {
                    ...state,
                    showIsDisabledFilter: action.isVisible,
                };
            case 'ReceiveEmails':
                return {
                    ...state,
                    emailFilterQuery: action.query,
                };
            default:
                throw new Error("Unexpected action");
        }
    }

    const [currentUserTableState, dispatchUser] = React.useReducer(userReducer, initialUserTableState);

    React.useEffect(() => {
        const fetchEmails = async () => {
            const emailsQuery = await legacyClient.getUsers(currentUserTableState.emailFilterQuery);

            dispatchUser({ type: 'ReceiveEmails', query: emailsQuery });
        }

        fetchEmails();
    }, [])

    const handlePage = (page: number) => {
        !isLoading && dispatch(PageAction.create(page));
    }

    const handleSort = (member: string) => {
        dispatch(SortAction.create(member));
    }

    const handleFilter = (member: keyof(User)) => {
        switch (member) {
            case 'Email':
                dispatchUser({ type: 'SetEmailsFilterVisiblity', isVisible: true });
                break;
            case 'FirstName':
                dispatchUser({ type: 'SetFirstNameFilterVisiblity', isVisible: true });
                break;
            case 'LastName':
                dispatchUser({ type: 'SetLastNameFilterVisiblity', isVisible: true });
                break;
            case 'IsDisabled':
                dispatchUser({ type: 'SetIsDisabledFilterVisiblity', isVisible: true });
                break;
        }

    }

    const handleMultiSelectFilterChange = (member: keyof(User), values: string[]): void => {
        dispatch(FilterAction.create(member, QueryCondition.Equal, values));
    };

    const handleEmailFilterChange = (values): void =>
        handleMultiSelectFilterChange("Email", values.map(v => v.Email));

    const handleFirstNameFilterChange = (values): void =>
        handleMultiSelectFilterChange("FirstName", values.map(v => v.FirstName));

    const handleLastNameFilterChange = (values): void =>
        handleMultiSelectFilterChange("LastName", values.map(v => v.LastName)); 

    const handleIsDisabledFilterChange = (values: string[]): void =>
        handleMultiSelectFilterChange("IsDisabled", values);

    const handleFilterApply = () => {
        dispatchUser({ type: 'SetEmailsFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetFirstNameFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetLastNameFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetIsDisabledFilterVisiblity', isVisible: false });
    }

    const handleIsDisabledFilterApply = () => {
        const isDisabledFilterValues = getFilterValues("IsDisabled", QueryCondition.Equal);
        if (isDisabledFilterValues && isDisabledFilterValues.length > 0) {
            handleMultiSelectFilterChange("IsDisabled", isDisabledFilterValues);
        }
        handleFilterApply();
    }

    const handleFilterClose = (field: keyof User) => {
        handleMultiSelectFilterChange(field, []);
        handleFilterApply();
    }

    const getFilterValues = (member: string, condition: QueryCondition): any =>
        queryTableState.query &&
        queryTableState.query.Filters &&
        queryTableState.query.Filters.find(item => item.Member === member && item.Condition === condition) &&
        queryTableState.query.Filters.find(item => item.Member === member && item.Condition === condition).Values;

    const renderRoles = (row: User) => {
        return row.Roles.map(r => (`(${r.Study ? r.Study.Name : 'All Studies'}) ${r.Name}`)).join(', ');
    }

    const renderStatus = (row: User) => {
        return row.IsDisabled ? <i className="text-danger fas fa-times"></i> : <i className="text-success fas fa-check"></i>;
    }

    const renderHamburgerMenu = (row: User) => {
        return (
            <HamburgerMenu>
                <GlobalAuthorization permissionId={PermissionEnum.EditUsers} currentPermissions={currentUserPermissions}>
                    <Condition test={() => !row.IsServiceAccount}>
                        <li>
                            <Link to={`/users/edit/${row.Id}`}>Edit</Link>
                        </li>
                    </Condition>
                </GlobalAuthorization>
            </HamburgerMenu>
        );
    }

    return (
        <>
            <QueryTable id="usersTable" query={queryTableState.query} rowUniqueIdentifier={row => row.Id} onFilter={handleFilter} onSort={(m) => handleSort(m)} onPage={(p) => handlePage(p)}>
                <QueryTableColumn member="Email" header="Email" map={row => row.Email} sortable={true} filterable={true} />
                <QueryTableColumn member="FirstName" header="First Name" map={row => row.FirstName} sortable={true} filterable={true} />
                <QueryTableColumn member="LastName" header="Last Name" map={row => row.LastName} sortable={true} filterable={true} />
                <QueryTableColumn member="Roles.Name" header="Roles" map={row => renderRoles(row)} sortable={false} filterable={false} />
                <QueryTableColumn member="IsDisabled" header="Enabled" map={row => renderStatus(row)} sortable={true} filterable={true} />
                <QueryTableColumn member="" header="" map={row => renderHamburgerMenu(row)} sortable={false} filterable={false} />
            </QueryTable>
            <MultiSelectModal
                header="Email Filter"
                show={currentUserTableState.showEmailFilter}
                options={currentUserTableState.emailFilterQuery.Data}
                valueKey={"Email"}
                labelKey={"Email"}
                values={getFilterValues("Email", QueryCondition.Equal)}
                onChange={handleEmailFilterChange}
                onApply={handleFilterApply}
                onClose={() => handleFilterClose("Email")}
            />
            <MultiSelectModal
                header="First Name Filter"
                show={currentUserTableState.showFirstNameFilter}
                options={currentUserTableState.emailFilterQuery?.Data ? uniqBy(currentUserTableState.emailFilterQuery?.Data, (a,b) => a.FirstName === b.FirstName) : []}
                valueKey={"FirstName"}
                labelKey={"FirstName"}
                values={getFilterValues("FirstName", QueryCondition.Equal)}
                onChange={handleFirstNameFilterChange}
                onApply={handleFilterApply}
                onClose={() => handleFilterClose("FirstName")}
            />
            <MultiSelectModal
                header="Last Name Filter"
                show={currentUserTableState.showLastNameFilter}
                options={currentUserTableState.emailFilterQuery?.Data ? uniqBy(currentUserTableState.emailFilterQuery?.Data, (a,b) => a.LastName === b.LastName) : []}
                valueKey={"LastName"}
                labelKey={"LastName"}
                values={getFilterValues("LastName", QueryCondition.Equal)}
                onChange={handleLastNameFilterChange}
                onApply={handleFilterApply}
                onClose={() => handleFilterClose("LastName")}
            />

            <BooleanSelectModal
                header="Enabled Filter"
                options={[
                    { id: true, name: 'No' },
                    { id: false, name: 'Yes' },
                ]}
                show={currentUserTableState.showIsDisabledFilter}
                value={getFilterValues("IsDisabled", QueryCondition.Equal)}
                onChange={handleIsDisabledFilterChange}
                onApply={handleIsDisabledFilterApply}
                onClear={() => handleFilterClose("IsDisabled")}
                onClose={() => handleFilterClose("IsDisabled")}
            />
        </>
    );
}

function mapStateToProps(state: MainState) {
    return {
        currentStudyId: state.security.studyId,
    }
}

export default connect(mapStateToProps)(UserListTable);
